// Headset sign-in by short code + link (supabase/functions/device-link).
//   Headset: startDeviceLink() -> show code + link; pollDeviceLink() every ~2 s;
//            on 'approved', finishDeviceSignIn(tokenHash) signs this browser in.
//   Phone / computer (/link): approveDeviceLink(code) while signed in.
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

const FUNCTION = 'device-link';

export interface DeviceLinkStart { code: string; secret: string; expiresAt: string; linkUrl: string }
export type DeviceLinkPoll =
  | { status: 'pending'; expiresAt: string }
  | { status: 'expired' }
  | { status: 'consumed' }
  | { status: 'approved'; tokenHash: string; email: string | null }
  | { status: 'error'; message: string };
export type ApproveError = 'signin_required' | 'bad_code' | 'not_found' | 'expired' | 'used' | 'rate_limited' | 'server_error';
export type DeviceLinkApprove = { ok: true; email: string | null } | { ok: false; error: ApproveError; message: string };

async function call(body: Record<string, unknown>): Promise<{ data: any; status: number }> {
  const { data, error } = await supabase.functions.invoke(FUNCTION, { body });
  if (!error) return { data, status: 200 };
  if (error instanceof FunctionsHttpError) {
    const res = error.context as Response;
    return { data: await res.json().catch(() => null), status: res.status };
  }
  return { data: null, status: 0 }; // network
}

export async function startDeviceLink(): Promise<DeviceLinkStart | { error: string }> {
  const { data } = await call({ action: 'start' });
  if (data?.code && data?.secret) return data as DeviceLinkStart;
  return { error: data?.message || 'Could not get a sign-in code. Check your connection.' };
}

export async function pollDeviceLink(code: string, secret: string): Promise<DeviceLinkPoll> {
  const { data, status } = await call({ action: 'poll', code, secret });
  if (data?.status === 'approved' && data.tokenHash) return { status: 'approved', tokenHash: data.tokenHash, email: data.email ?? null };
  if (data?.status === 'pending') return { status: 'pending', expiresAt: data.expiresAt };
  if (data?.status === 'expired' || data?.status === 'consumed') return { status: data.status };
  if (status === 403) return { status: 'expired' }; // row swept: start over
  return { status: 'error', message: data?.message || 'Connection trouble…' };
}

export async function approveDeviceLink(code: string): Promise<DeviceLinkApprove> {
  const { data, status } = await call({ action: 'approve', code });
  if (data?.ok) return { ok: true, email: data.email ?? null };
  return {
    ok: false,
    error: (data?.error as ApproveError) || 'server_error',
    message: data?.message || (status === 0 ? 'No connection. Check your internet and try again.' : 'Something went wrong. Try again.'),
  };
}

/** Completes the one-time sign-in the poll returned (no email involved). */
export async function finishDeviceSignIn(tokenHash: string): Promise<string | null> {
  const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
  return error ? error.message : null;
}
